import { BaseLayout } from '@/components/layout/BaseLayout'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useStore } from '@/features/stores/StoreContext'
import { supabase } from '@/lib/supabase/client'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Plus, Pencil, Trash2, FolderTree, Image } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

interface CategoryPage {
  id: string
  category_id: string
  banner_image: string | null
  description: string | null
  seo_title: string | null
  seo_description: string | null
  layout: string
  sort_order: string
  categories: { name: string }
}

export function CategoryPagesPage() {
  const { currentStore } = useStore()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editingPage, setEditingPage] = useState<CategoryPage | null>(null)
  const [formData, setFormData] = useState({
    category_id: '',
    banner_image: '',
    description: '',
    seo_title: '',
    seo_description: '',
    layout: 'grid',
    sort_order: 'newest',
  })

  const { data: categories = [] } = useQuery({
    queryKey: ['categories', currentStore?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name')
        .eq('store_id', currentStore?.id)
        .order('name')
      if (error) throw error
      return data
    },
    enabled: !!currentStore?.id,
  })

  const { data: pages = [], isLoading } = useQuery({
    queryKey: ['category-pages', currentStore?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('category_pages')
        .select('*, categories(name)')
        .eq('store_id', currentStore?.id)
      if (error) throw error
      return data as CategoryPage[]
    },
    enabled: !!currentStore?.id,
  })

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await supabase.from('category_pages').insert({
        store_id: currentStore?.id,
        ...data,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-pages'] })
      toast.success('Category page created')
      handleClose()
    },
    onError: () => toast.error('Failed to create category page'),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const { error } = await supabase.from('category_pages').update(data).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-pages'] })
      toast.success('Category page updated')
      handleClose()
    },
    onError: () => toast.error('Failed to update category page'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('category_pages').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-pages'] })
      toast.success('Category page deleted')
    },
    onError: () => toast.error('Failed to delete category page'),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.category_id) {
      toast.error('Category is required')
      return
    }
    if (editingPage) {
      updateMutation.mutate({ id: editingPage.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleEdit = (page: CategoryPage) => {
    setEditingPage(page)
    setFormData({
      category_id: page.category_id,
      banner_image: page.banner_image || '',
      description: page.description || '',
      seo_title: page.seo_title || '',
      seo_description: page.seo_description || '',
      layout: page.layout,
      sort_order: page.sort_order,
    })
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setEditingPage(null)
    setFormData({ category_id: '', banner_image: '', description: '', seo_title: '', seo_description: '', layout: 'grid', sort_order: 'newest' })
  }

  return (
    <BaseLayout>
      <div className="p-8">
        <PageHeader
          title="Category Pages"
          description="Customize category landing pages"
          action={
            <Button onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Page
            </Button>
          }
        />

        {isLoading ? (
          <LoadingSpinner />
        ) : pages.length === 0 ? (
          <EmptyState
            icon={FolderTree}
            title="No category pages yet"
            description="Create custom landing pages for your categories"
            action={{ label: 'Create Page', onClick: () => setOpen(true) }}
          />
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {pages.map((page) => (
              <div key={page.id} className="bg-white rounded-lg border overflow-hidden">
                {page.banner_image ? (
                  <img src={page.banner_image} alt={page.categories.name} className="w-full h-40 object-cover" />
                ) : (
                  <div className="w-full h-40 bg-muted flex items-center justify-center">
                    <Image className="h-12 w-12 text-muted-foreground" />
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-semibold mb-2">{page.categories.name}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {page.description || 'No description'}
                  </p>
                  <div className="flex gap-2 text-xs mb-3">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">{page.layout}</span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">{page.sort_order}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(page)} className="flex-1">
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteMutation.mutate(page.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingPage ? 'Edit Category Page' : 'Create Category Page'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Category *</Label>
                <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat: any) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Banner Image URL</Label>
                <Input
                  value={formData.banner_image}
                  onChange={(e) => setFormData({ ...formData, banner_image: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Category description"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Layout</Label>
                  <Select value={formData.layout} onValueChange={(v) => setFormData({ ...formData, layout: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="grid">Grid</SelectItem>
                      <SelectItem value="list">List</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sort Order</Label>
                  <Select value={formData.sort_order} onValueChange={(v) => setFormData({ ...formData, sort_order: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Newest</SelectItem>
                      <SelectItem value="price_asc">Price: Low to High</SelectItem>
                      <SelectItem value="price_desc">Price: High to Low</SelectItem>
                      <SelectItem value="popular">Most Popular</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>SEO Title</Label>
                <Input
                  value={formData.seo_title}
                  onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                  placeholder="SEO optimized title"
                />
              </div>
              <div>
                <Label>SEO Description</Label>
                <Textarea
                  value={formData.seo_description}
                  onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                  placeholder="SEO meta description"
                  rows={2}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {editingPage ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </BaseLayout>
  )
}
